import React, { useState, useEffect } from 'react';
import { getData, postData, putData, deleteData } from '../helpers/ApiSettings';

const SaleCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    order: 0,
    is_private: false,
  });

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await getData('/sale-categories?sort[0]=order:asc&sort[1]=name:asc');
      setCategories(response.data.data);
      setError(null);
    } catch (err) {
      setError('Erreur lors du chargement des catégories de vente');
      console.error('Error fetching sale categories:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'order' ? parseInt(value, 10) || 0 : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await putData(`/sale-categories/${editingCategory.documentId}`, { data: formData });
        alert('Catégorie modifiée avec succès');
      } else {
        await postData('/sale-categories', { data: formData });
        alert('Catégorie créée avec succès');
      }

      const modal = document.getElementById('saleCategoryModal');
      const bootstrapModal = window.bootstrap.Modal.getInstance(modal);
      bootstrapModal.hide();

      setFormData({ name: '', description: '', order: 0, is_private: false });
      setEditingCategory(null);
      await fetchCategories();
    } catch (err) {
      alert('Erreur lors de l\'enregistrement de la catégorie');
      console.error('Error saving sale category:', err);
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description || '',
      order: category.order ?? 0,
      is_private: category.is_private ?? false,
    });
    const modal = new window.bootstrap.Modal(document.getElementById('saleCategoryModal'));
    modal.show();
  };

  const handleDelete = async (documentId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette catégorie ? Les produits qui l\'utilisent perdront leur catégorie.')) {
      try {
        await deleteData(`/sale-categories/${documentId}`);
        await fetchCategories();
        alert('Catégorie supprimée avec succès');
      } catch (err) {
        alert('Erreur lors de la suppression de la catégorie');
        console.error('Error deleting sale category:', err);
      }
    }
  };

  const handleAddNew = () => {
    setEditingCategory(null);
    setFormData({ name: '', description: '', order: 0 });
    const modal = new window.bootstrap.Modal(document.getElementById('saleCategoryModal'));
    modal.show();
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="h4 mb-0">Catégories de vente</h2>
        <div>
          <button onClick={fetchCategories} className="btn btn-outline-primary me-2">
            Actualiser
          </button>
          <button onClick={handleAddNew} className="btn btn-primary">
            Nouvelle Catégorie
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          {categories.length === 0 ? (
            <p className="text-center text-muted my-5">Aucune catégorie pour le moment</p>
          ) : (
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    <th style={{ width: '80px' }}>Ordre</th>
                    <th>Nom</th>
                    <th>Description</th>
                    <th style={{ width: '140px' }}>Visibilité</th>
                    <th style={{ width: '200px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map((category) => (
                    <tr key={category.id}>
                      <td>{category.order ?? 0}</td>
                      <td>{category.name}</td>
                      <td>{category.description || '-'}</td>
                      <td>
                        {category.is_private ? (
                          <span className="badge bg-warning text-dark">Tarif vente privée actif</span>
                        ) : (
                          <span className="badge bg-light text-muted">Standard</span>
                        )}
                      </td>
                      <td>
                        <div className="btn-group">
                          <button
                            className="btn btn-sm btn-primary me-2"
                            onClick={() => handleEdit(category)}
                          >
                            Modifier
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDelete(category.documentId)}
                          >
                            Supprimer
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="modal fade" id="saleCategoryModal" tabIndex="-1">
        <div className="modal-dialog">
          <div className="modal-content">
            <form onSubmit={handleSubmit}>
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingCategory ? 'Modifier la Catégorie' : 'Nouvelle Catégorie'}
                </h5>
                <button type="button" className="btn-close" data-bs-dismiss="modal"></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label htmlFor="name" className="form-label">Nom</label>
                  <input
                    type="text"
                    className="form-control"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="description" className="form-label">Description</label>
                  <textarea
                    className="form-control"
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows="3"
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="order" className="form-label">Ordre d'affichage</label>
                  <input
                    type="number"
                    className="form-control"
                    id="order"
                    name="order"
                    value={formData.order}
                    onChange={handleInputChange}
                  />
                  <small className="text-muted">Plus petit = affiché en premier</small>
                </div>
                <div className="mb-3">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="is_private"
                      checked={formData.is_private}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_private: e.target.checked }))}
                    />
                    <label className="form-check-label" htmlFor="is_private">
                      Tarif vente privée pour cette catégorie
                    </label>
                  </div>
                  <small className="text-muted">
                    Si activé, tous les produits de cette catégorie proposeront automatiquement un tarif vente privée à vos clients référencés (en plus du prix public).
                  </small>
                  {formData.is_private && (
                    <div className="alert alert-info py-2 px-3 small mt-2 mb-0">
                      Les produits de cette catégorie resteront visibles par tout le monde. Les clients référencés (ayant passé une commande) verront en plus le tarif vente privée du produit et paieront à ce tarif.
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">
                  Annuler
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingCategory ? 'Mettre à jour' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SaleCategories;
